import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Joyride, { Step } from 'react-joyride';

interface OnboardingJoyrideProps {
  run: boolean;
  onFinish: () => void;
}

// Map each step index to the route it should be shown on
const stepRoutes: Record<number, string> = {
  0: '/app/dashboard',           // Welcome
  1: '/app/dashboard',           // Project & Documentation Repository
  2: '/app/dashboard',           // Navigation (sidebar is on every page, but start on dashboard)
  3: '/app/documents',           // Document Ingestion
  4: '/app/sop-builder',         // SOP Builder
  5: '/app/process-maps',        // Process Maps
  6: '/app/risk-mapping',        // Risk Mapping & Compliance
  7: '/app/process-optimization',// Process Optimization
  8: '/app/intelligence',        // Jarvis Intelligence
  9: '/app/executive-dashboard', // Executive Dashboard
  10: '/app/ai-council',         // AI Council
  11: '/app/smart-start',        // Help button (back to Smart Router)
  12: '/app/smart-start',        // Getting Started Checklist
};

const OnboardingJoyride: React.FC<OnboardingJoyrideProps> = ({ run, onFinish }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Wait for all target elements to be available
  useEffect(() => {
    if (!run) {
      setIsReady(false);
      return;
    }

    const checkTargets = () => {
      const basicTargets = [
        '[data-tour="navigation"]',
        '[data-tour="help-button"]'
      ];

      const basicTargetsExist = basicTargets.every(selector => {
        const element = document.querySelector(selector);
        return element !== null;
      });

      if (basicTargetsExist) {
        setIsReady(true);
      } else {
        setTimeout(checkTargets, 100);
      }
    };

    setTimeout(checkTargets, 250);
  }, [run]);

  // Navigate to the correct page for the current step
  const navigateToStep = useCallback((nextStepIndex: number) => {
    const targetRoute = stepRoutes[nextStepIndex];
    if (targetRoute && location.pathname !== targetRoute) {
      setIsNavigating(true);
      navigate(targetRoute);
      // Wait for the page to render before showing the step
      setTimeout(() => {
        setStepIndex(nextStepIndex);
        setIsNavigating(false);
      }, 600);
    } else {
      setStepIndex(nextStepIndex);
    }
  }, [location.pathname, navigate]);

  const steps: Step[] = [
    // Step 0: Welcome
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-super-dark-purple mb-4">Welcome to JARVIS RCSA!</h2>
          <p className="text-gray-600 mb-4">
            Your AI-powered consulting suite. Let's walk through the complete
            platform — we'll navigate you to each section automatically.
          </p>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-super-dark-purple mb-2">What we'll cover:</h4>
            <div className="text-left space-y-1.5 text-sm text-gray-700">
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">1.</span>
                Project & Documentation Repository
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">2.</span>
                Platform Navigation
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">3.</span>
                Document Ingestion
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">4.</span>
                SOP Builder
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">5.</span>
                Process Maps
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">6.</span>
                Risk Mapping & Compliance
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">7.</span>
                Process Optimization
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">8.</span>
                Jarvis Intelligence
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">9.</span>
                Executive Dashboard
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">10.</span>
                AI Council
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">11.</span>
                Help & Support
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2 font-medium">12.</span>
                Getting Started Checklist
              </div>
            </div>
          </div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    // Step 1: Project & Documentation Repository (Dashboard page)
    {
      target: '[data-tour="new-project-button"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Project & Documentation Repository</h3>
          <p className="text-gray-600 mb-3">
            Everything starts here. Create and manage your risk assessment projects, organize documentation,
            and track assessment progress across your organization.
          </p>
          <div className="bg-blue-50 rounded-lg p-3 mt-3 space-y-2">
            <p className="text-sm text-blue-800">
              <strong>Key Features:</strong>
            </p>
            <div className="text-sm text-blue-700 space-y-1">
              <div>• Create projects for departments, audits, or assessments</div>
              <div>• Organize and version your compliance documents</div>
              <div>• Track project progress with status indicators</div>
              <div>• Collaborate with team members across projects</div>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 mt-2">
            <p className="text-sm text-green-800">
              <strong>Try it:</strong> Click "New Project" to create your first risk assessment project.
            </p>
          </div>
        </div>
      ),
      placement: 'bottom'
    },
    // Step 2: Platform Navigation (Dashboard page)
    {
      target: '[data-tour="navigation"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Platform Navigation</h3>
          <p className="text-gray-600 mb-3">
            The sidebar gives you access to all platform modules. We'll now take you through each one:
          </p>
          <div className="space-y-2 mt-3">
            <div className="flex items-center bg-blue-50 rounded p-2">
              <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
              <span className="text-sm text-blue-800"><strong>Project Repository</strong> - Projects & documentation</span>
            </div>
            <div className="flex items-center bg-green-50 rounded p-2">
              <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
              <span className="text-sm text-green-800"><strong>Document Ingestion</strong> - Upload & AI processing</span>
            </div>
            <div className="flex items-center bg-indigo-50 rounded p-2">
              <div className="w-3 h-3 bg-indigo-500 rounded mr-2"></div>
              <span className="text-sm text-indigo-800"><strong>SOP Builder</strong> - Generate procedures</span>
            </div>
            <div className="flex items-center bg-cyan-50 rounded p-2">
              <div className="w-3 h-3 bg-cyan-500 rounded mr-2"></div>
              <span className="text-sm text-cyan-800"><strong>Process Maps</strong> - Visual BPMN workflows</span>
            </div>
            <div className="flex items-center bg-red-50 rounded p-2">
              <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
              <span className="text-sm text-red-800"><strong>Risk Mapping</strong> - Risk & control assessment</span>
            </div>
            <div className="flex items-center bg-yellow-50 rounded p-2">
              <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
              <span className="text-sm text-yellow-800"><strong>Process Optimization</strong> - Efficiency analysis</span>
            </div>
            <div className="flex items-center bg-purple-50 rounded p-2">
              <div className="w-3 h-3 bg-purple-500 rounded mr-2"></div>
              <span className="text-sm text-purple-800"><strong>Intelligence, Dashboard & AI Council</strong></span>
            </div>
          </div>
        </div>
      ),
      placement: 'right'
    },
    // Step 3: Document Ingestion (Documents page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the Document Ingestion page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Document Ingestion</h3>
          <p className="text-gray-600 mb-3">
            Upload your policies, procedures, risk frameworks, and compliance documents.
            JARVIS uses AI to automatically extract and analyze all content.
          </p>
          <div className="bg-green-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-green-800 mb-2">Supported Formats:</h4>
            <div className="text-sm text-green-700 space-y-1">
              <div>• <strong>PDF:</strong> Policies, audit reports, compliance docs (including encrypted/password-protected)</div>
              <div>• <strong>DOCX:</strong> Procedures, SOPs, process narratives</div>
              <div>• <strong>XLSX:</strong> Risk registers, control matrices, data tables</div>
              <div>• <strong>PPTX:</strong> Presentations, training materials</div>
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 mt-3">
            <h4 className="font-semibold text-purple-800 mb-2">AI Auto-Analysis:</h4>
            <div className="text-sm text-purple-700 space-y-1">
              <div>• Process step identification & stakeholder mapping</div>
              <div>• Risk and control extraction & mapping</div>
              <div>• Compliance framework alignment</div>
              <div>• Key terminology & entity extraction</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 4: SOP Builder (SOP Builder page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-indigo-100 text-indigo-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the SOP Builder page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">SOP Builder</h3>
          <p className="text-gray-600 mb-3">
            Generate and manage Standard Operating Procedures automatically from your uploaded documents, or create them from scratch with AI assistance.
          </p>
          <div className="bg-indigo-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-indigo-800 mb-2">Key Features:</h4>
            <div className="text-sm text-indigo-700 space-y-1">
              <div>• <strong>AI-Generated SOPs:</strong> Automatically create procedures from your policy documents</div>
              <div>• <strong>Rich Text Editor:</strong> Full-featured editor with formatting and templates</div>
              <div>• <strong>Version Control:</strong> Track changes with approval workflows</div>
              <div>• <strong>Multi-Format Export:</strong> Export to PDF, Word, Excel, and PowerPoint</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 5: Process Maps (Process Maps page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-cyan-100 text-cyan-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the Process Maps page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Process Maps (BPMN Editor)</h3>
          <p className="text-gray-600 mb-3">
            Visualize and edit your organizational processes with a powerful drag-and-drop BPMN editor.
            Auto-generated from AI analysis or built from scratch.
          </p>
          <div className="bg-cyan-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-cyan-800 mb-2">Key Features:</h4>
            <div className="text-sm text-cyan-700 space-y-1">
              <div>• <strong>Drag & Drop Editor:</strong> Interactive BPMN elements, swimlanes, and gateways</div>
              <div>• <strong>AI Auto-Generation:</strong> Process flows extracted from your documents</div>
              <div>• <strong>Stakeholder Assignment:</strong> Assign responsible parties to each step</div>
              <div>• <strong>RACI Matrix:</strong> Auto-generated responsibility matrices</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 6: Risk Mapping (Risk Mapping page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-red-100 text-red-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the Risk Mapping page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Risk Mapping & Compliance</h3>
          <p className="text-gray-600 mb-3">
            The core of RCSA — comprehensive risk identification, control mapping, and compliance assessment.
          </p>
          <div className="bg-red-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-red-800 mb-2">Risk Assessment Features:</h4>
            <div className="text-sm text-red-700 space-y-1">
              <div>• <strong>4-Level Risk Hierarchy:</strong> Strategic → Department → Sub-process → Control level</div>
              <div>• <strong>Risk Scoring:</strong> Inherent vs. Residual risk calculations</div>
              <div>• <strong>Control Mapping:</strong> Link controls to risks with effectiveness ratings</div>
              <div>• <strong>Gap Analysis:</strong> Identify missing controls and remediation needs</div>
              <div>• <strong>Heat Maps:</strong> Visual risk distribution and prioritization</div>
              <div>• <strong>Compliance Alignment:</strong> Map to COSO, ISO 31000, SOC 2, ISO 27001</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 7: Process Optimization (Process Optimization page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the Process Optimization page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Process Optimization</h3>
          <p className="text-gray-600 mb-3">
            Analyze and improve your organizational processes with AI-driven recommendations.
          </p>
          <div className="bg-yellow-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-yellow-800 mb-2">Optimization Capabilities:</h4>
            <div className="text-sm text-yellow-700 space-y-1">
              <div>• <strong>Efficiency Analysis:</strong> Identify bottlenecks and redundancies</div>
              <div>• <strong>Cost Analysis:</strong> Resource allocation and cost optimization</div>
              <div>• <strong>Automation Opportunities:</strong> AI suggests steps to automate</div>
              <div>• <strong>Benchmarking:</strong> Compare processes against best practices</div>
              <div>• <strong>Recommendations:</strong> Actionable improvement suggestions with impact scoring</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 8: Jarvis Intelligence (Intelligence page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the Jarvis Intelligence page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Jarvis Intelligence</h3>
          <p className="text-gray-600 mb-3">
            Advanced AI-powered analytics providing deep insights across your entire risk landscape.
          </p>
          <div className="bg-purple-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-purple-800 mb-2">Intelligence Features:</h4>
            <div className="text-sm text-purple-700 space-y-1">
              <div>• <strong>Trend Analysis:</strong> AI-powered insights and risk trend detection</div>
              <div>• <strong>Cross-Project Intelligence:</strong> Pattern detection across all projects</div>
              <div>• <strong>Predictive Indicators:</strong> Early warning signals for emerging risks</div>
              <div>• <strong>Smart Search:</strong> Semantic search across all documents and analyses</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 9: Executive Dashboard (Executive Dashboard page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the Executive Dashboard page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Executive Dashboard</h3>
          <p className="text-gray-600 mb-3">
            Executive-level visibility into your organization's risk posture and compliance status.
          </p>
          <div className="bg-emerald-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-emerald-800 mb-2">Dashboard Features:</h4>
            <div className="text-sm text-emerald-700 space-y-1">
              <div>• <strong>KPI Visualization:</strong> Interactive charts and key metrics</div>
              <div>• <strong>Risk Appetite Monitoring:</strong> Track risk levels against appetite thresholds</div>
              <div>• <strong>Compliance Status:</strong> Framework alignment across SOC 2, ISO 27001, and more</div>
              <div>• <strong>Team Performance:</strong> Assessment progress and team activity tracking</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 10: AI Council (AI Council page)
    {
      target: 'body',
      content: (
        <div className="text-center">
          <div className="inline-flex items-center bg-amber-100 text-amber-800 text-xs font-medium px-3 py-1 rounded-full mb-3">
            You are now on the AI Council page
          </div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">AI Council</h3>
          <p className="text-gray-600 mb-3">
            Leverage multiple AI providers for collaborative analysis, natural language Q&A, and guided interviews.
          </p>
          <div className="bg-amber-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-amber-800 mb-2">AI Council Features:</h4>
            <div className="text-sm text-amber-700 space-y-1">
              <div>• <strong>Multi-AI Providers:</strong> Claude, GPT-4, and Gemini working together</div>
              <div>• <strong>Collaborative Analysis:</strong> Deeper insights from multiple AI perspectives</div>
              <div>• <strong>Natural Language Q&A:</strong> Ask questions about risks, controls, and processes</div>
              <div>• <strong>Guided Interviews:</strong> AI-assisted stakeholder input sessions</div>
            </div>
          </div>
          <div className="bg-teal-50 rounded-lg p-3 mt-3">
            <h4 className="font-semibold text-teal-800 mb-2">Evidence Collection:</h4>
            <div className="text-sm text-teal-700 space-y-1">
              <div>• Automated evidence harvesting & screenshot capture</div>
              <div>• Scheduled auto-capture for continuous monitoring</div>
              <div>• Audit-ready PDF generation with embedded metadata</div>
            </div>
          </div>
        </div>
      ),
      placement: 'center'
    },
    // Step 11: Help Button (back on Dashboard)
    {
      target: '[data-tour="help-button"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Need Help Anytime?</h3>
          <p className="text-gray-600 mb-3">
            You can always replay this tutorial or get help by clicking the Help button in the header.
          </p>
          <div className="bg-yellow-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-yellow-800">
              <strong>Remember:</strong> This tutorial is always available when you need a refresher!
            </p>
          </div>
        </div>
      ),
      placement: 'bottom-start'
    },
    // Step 12: Getting Started Checklist
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-super-dark-purple mb-4">You're Ready to Go!</h2>
          <p className="text-gray-600 mb-4">
            You've toured every section of the JARVIS RCSA platform. Here's your recommended workflow
            to get started:
          </p>
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-gray-800 mb-3">Your Getting Started Checklist:</h4>
            <div className="text-left space-y-2 text-sm text-gray-700">
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">1.</span>
                Create your first project in the Dashboard
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">2.</span>
                Upload policy and procedure documents
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">3.</span>
                Run AI analysis to extract processes and risks
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">4.</span>
                Review generated SOPs and process maps
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">5.</span>
                Explore risk mapping and control assessments
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">6.</span>
                Use Process Optimization for improvement insights
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2 font-bold">7.</span>
                Monitor everything from the Executive Dashboard
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Ready to transform your risk assessment workflow!
          </div>
        </div>
      ),
      placement: 'center',
    }
  ];

  const handleJoyrideCallback = (data: any) => {
    const { status, type, index, action } = data;

    if (status === 'finished' || status === 'skipped') {
      setStepIndex(0);
      navigate('/app/smart-start');
      onFinish();
    } else if (type === 'step:after') {
      const nextIndex = action === 'prev' ? index - 1 : index + 1;
      if (nextIndex >= 0 && nextIndex < steps.length) {
        navigateToStep(nextIndex);
      }
    } else if (status === 'error') {
      const nextIndex = index + 1;
      if (nextIndex < steps.length) {
        navigateToStep(nextIndex);
      }
    }
  };

  return (
    <>
      {/* Navigation loading overlay */}
      {isNavigating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-blue"></div>
            <span className="text-super-dark-purple font-medium">Navigating to next section...</span>
          </div>
        </div>
      )}
      <Joyride
        callback={handleJoyrideCallback}
        continuous={true}
        run={run && isReady && !isNavigating}
        scrollToFirstStep={true}
        showProgress={true}
        showSkipButton={true}
        stepIndex={stepIndex}
        steps={steps}
        styles={{
          options: {
            primaryColor: '#57C8F4',
            textColor: '#333',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 10000,
          }
        }}
        locale={{
          back: 'Previous',
          close: 'Close',
          last: 'Get Started!',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />
    </>
  );
};

export default OnboardingJoyride;
